import { _, helpers, React, ts, ui } from '_core';

import { percentColumns, transform } from './helpers';
import { Params } from './types';

type ChartProps = {
  id: string;
  readOnly: boolean;
  widgetKey: string;
  loadWidgetData: (_payload: ts.types.widgets.WidgetGetDataParams) => Promise<ts.types.widgets.WidgetGetDataResponse>;
  summary: string;
  params: Params;
  fullScreen: boolean;
  title: string;
  formOptions: { key: string; label: string }[];
};

const Chart = ({ id, readOnly, loadWidgetData, summary, fullScreen, params, title, formOptions }: ChartProps) => {
  const [data, setData] = React.useState<ts.types.widgets.TableData>(null);
  const [error, setError] = React.useState<ts.types.common.ApiError>(null);

  const loadData = async () => {
    try {
      const response = await loadWidgetData({
        data: {
          file: summary,
        },
      });

      if (response) {
        setData(response.data?.map((el, idx) => ({ ...el, highlighted: idx === 0 })));
      }
    } catch (err) {
      setError(err as ts.types.common.ApiError);
    }
  };

  React.useEffect(() => {
    if (summary) loadData();
    return () => setData(null);
  }, [summary]);

  const currField = formOptions.find((opt) => opt.key == params.selectedParam);

  const dataKeys = React.useMemo(() => {
    if (!data?.[0]) return [];
    return Object.keys(data[0])
      .filter((key) => key !== params.metric)
      .map((key) => ({
        key,
        label: formOptions.find((opt) => opt.key == key)?.label,
      }));
  }, [data, params.metric, formOptions]);

  const tooltip = React.useMemo(() => {
    const metricFormat = percentColumns.includes(params.metric) ? `{${params.metric}_percent}%` : `{${params.metric}}`;
    const tooltipParts = [];
    tooltipParts.push(`[bold]Metric:[/] ${metricFormat}`);
    tooltipParts.push(
      ...dataKeys.filter((item) => !!item.label).map((item) => `[bold]${item.label}:[/] {${item.key}}`)
    );
    return tooltipParts.join('\n');
  }, [params.metric, dataKeys]);

  const formattedData = React.useMemo(() => {
    if (!data) return null;
    return transform(data as ts.types.widgets.ScatterData, params.metric);
  }, [data, params.metric]);

  const highlightedIndex = React.useMemo(() => {
    if (_.isEmpty(data)) return undefined;
    const indexOfId = data.findIndex((row) => row?.trial_id === 'trial_base');
    return indexOfId < 0 ? undefined : indexOfId;
  }, [data]);

  return (
    <ui.ScatterPlotChart
      id={id}
      fullScreen={fullScreen}
      readOnly={readOnly}
      data={formattedData}
      alert={error ? { type: 'error', message: helpers.parseApiError(error) } : undefined}
      xAxisField={currField.key}
      yAxisField={params.metric}
      lineSeriesName={_.startCase(params.selectedParam)}
      exportTitle={title}
      numberFormat="#.####"
      yAxisFormatter={percentColumns.includes(params.metric) ? '#.##%' : null}
      xAxisFormatter={null}
      hideLegend
      xAxisLabel={currField.label}
      yAxisLabel={_.startCase(params.metric)}
      elementTooltipText={tooltip}
      highlightedIndex={highlightedIndex}
      highlightedText="Base Backtest"
    />
  );
};

export default Chart;
